import { Link, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import { useI18n } from "../lib/i18n";

const C = {
  features: {
    en: ["Everything inside Studio Movies IA", "One product from idea to final film.",
      [["AI Director", "A concept becomes a logline, synopsis, acts, sequences, shots, camera and lighting direction."],
       ["Production Bible", "Characters, locations, props, visual style, camera language, colour, wardrobe, sound and continuity rules — persistent context for every generation."],
       ["Character & Location Lock", "Reusable identities with codes (CHAR-0001, LOC-0001) automatically injected into prompts."],
       ["Shot Director", "Shot type, framing, camera, lens, movement, subject, action, environment, lighting, mood, negatives."],
       ["Performance Director", "Emotion, eye direction, posture, movement speed and intention per character."],
       ["Storyboard engine", "Approve image frames before promoting them to video shots."],
       ["Sequence builder", "Group shots into sequences; generate the whole sequence with continuity."],
       ["Multi-track timeline", "Video, b-roll, music, voice, SFX, text, subtitles, VFX and colour tracks."],
       ["AI Editor", "Natural-language editing translated into structured timeline operations."],
       ["Continuity Engine", "Automated audit of wardrobe, light, props, location and camera axis."],
       ["Director's Cut", "Analyse the finished film and propose pacing, music and structural changes."],
       ["Versioning", "Per-shot V1…Vn and per-project V1 / Final / Director's Cut with restore."],
       ["Frame extraction", "Pull a frame from any video, edit it, animate it again."],
       ["Cinema Mode", "Distraction-free full-screen review with timecode comments."],
       ["Movies Credits", "Reserve → generate → consume, with automatic refunds on failure."],
       ["Model router", "Seedance, Veo and future engines behind one abstraction with automatic fallback."],
       ["Brand Kit", "Logos, colours, fonts, products, slogans and voice applied automatically."],
       ["Exports", "9:16, 1:1, 4:5, 16:9, 21:9 and 2.39:1 with subtitles and thumbnails."]]],
    fr: ["Tout ce que contient Studio Movies IA", "Un seul produit, de l'idée au film final.",
      [["AI Director", "Un concept devient logline, synopsis, actes, séquences, plans, direction caméra et lumière."],
       ["Production Bible", "Personnages, décors, accessoires, style visuel, langage caméra, couleur, costumes, son et règles de continuité — un contexte persistant pour chaque génération."],
       ["Character & Location Lock", "Identités réutilisables avec codes (CHAR-0001, LOC-0001) injectées automatiquement dans les prompts."],
       ["Shot Director", "Type de plan, cadre, caméra, optique, mouvement, sujet, action, environnement, lumière, ambiance, négatifs."],
       ["Performance Director", "Émotion, direction du regard, posture, vitesse et intention par personnage."],
       ["Moteur de storyboard", "Validez des images avant de les promouvoir en plans vidéo."],
       ["Sequence builder", "Groupez les plans en séquences et générez la séquence entière avec continuité."],
       ["Timeline multipiste", "Vidéo, b-roll, musique, voix, SFX, texte, sous-titres, VFX et étalonnage."],
       ["AI Editor", "Montage en langage naturel traduit en opérations structurées sur la timeline."],
       ["Continuity Engine", "Audit automatique des costumes, lumière, accessoires, décor et axe caméra."],
       ["Director's Cut", "Analyse du film terminé et propositions de rythme, musique et structure."],
       ["Versions", "V1…Vn par plan et V1 / Final / Director's Cut par projet, avec restauration."],
       ["Extraction d'image", "Extrayez une image d'une vidéo, éditez-la, réanimez-la."],
       ["Cinema Mode", "Visionnage plein écran sans distraction, avec commentaires au timecode."],
       ["Movies Credits", "Réservation → génération → consommation, remboursement automatique en cas d'échec."],
       ["Routeur de modèles", "Seedance, Veo et futurs moteurs derrière une abstraction avec fallback automatique."],
       ["Brand Kit", "Logos, couleurs, typographies, produits, slogans et voix appliqués automatiquement."],
       ["Exports", "9:16, 1:1, 4:5, 16:9, 21:9 et 2.39:1 avec sous-titres et miniatures."]]],
  },
  enterprise: {
    en: ["Enterprise & Production House", "Volume production, teams and integration.",
      [["Team management", "Owner, Director, Producer, Editor, Creator and Viewer roles with per-project permissions."],
       ["Large credit allocations", "Negotiated credit blocks with usage reporting per team and per project."],
       ["Priority queue", "Dedicated generation capacity so campaigns never wait behind consumer traffic."],
       ["API access", "POST /v1/generations, /v1/projects, /v1/scenes, /v1/sequences and GET status endpoints."],
       ["SSO-ready", "Architecture prepared for SAML/OIDC identity providers."],
       ["Audit logs", "Every generation, entitlement change and export recorded."],
       ["Custom commercial terms", "White-label exports and extended commercial rights."],
       ["Dedicated support", "Named contact and onboarding for your creative team."]]],
    fr: ["Entreprise & Production House", "Production en volume, équipes et intégration.",
      [["Gestion d'équipe", "Rôles Owner, Director, Producer, Editor, Creator et Viewer avec permissions par projet."],
       ["Allocations de crédits", "Blocs de crédits négociés avec reporting par équipe et par projet."],
       ["File prioritaire", "Capacité de génération dédiée : vos campagnes ne font pas la queue."],
       ["Accès API", "POST /v1/generations, /v1/projects, /v1/scenes, /v1/sequences et endpoints de statut."],
       ["Prêt pour le SSO", "Architecture préparée pour les fournisseurs d'identité SAML/OIDC."],
       ["Journaux d'audit", "Chaque génération, changement de droits et export est enregistré."],
       ["Conditions sur mesure", "Exports en marque blanche et droits commerciaux étendus."],
       ["Support dédié", "Un contact nommé et un onboarding pour votre équipe créative."]]],
  },
  "api-docs": {
    en: ["Studio Movies API", "Drive the studio from your own stack.",
      [["POST /v1/projects", "Create a project with type, duration target, aspect ratio and visual style."],
       ["POST /v1/generations", "Queue an image or video generation with prompt, references and quality tier."],
       ["GET /v1/generations/{id}", "Poll status: pending, queued, analyzing, generating, processing, completed, failed."],
       ["POST /v1/sequences", "Create a sequence and generate all of its shots with continuity."],
       ["Webhooks", "Receive generation.completed, generation.failed and export.ready events."],
       ["Authentication", "Bearer API keys scoped per organisation, available on the Studio plan and above."]]],
    fr: ["API Studio Movies", "Pilotez le studio depuis votre propre stack.",
      [["POST /v1/projects", "Créez un projet avec type, durée cible, ratio et style visuel."],
       ["POST /v1/generations", "Mettez en file une génération image ou vidéo avec prompt, références et palier de qualité."],
       ["GET /v1/generations/{id}", "Suivez le statut : pending, queued, analyzing, generating, processing, completed, failed."],
       ["POST /v1/sequences", "Créez une séquence et générez tous ses plans avec continuité."],
       ["Webhooks", "Recevez generation.completed, generation.failed et export.ready."],
       ["Authentification", "Clés API Bearer par organisation, disponibles à partir du plan Studio."]]],
  },
  about: {
    en: ["About Studio Movies IA", "We build the orchestration layer, not another model.",
      [["The thesis", "Video models will keep changing. Orchestration, UX, templates, prompts, credits, projects and distribution are what compound."],
       ["Provider independence", "Every model lives in a registry with its own cost, credit price and fallback. A supplier price change is a config edit."],
       ["Made for real production", "Acts, sequences, scenes, shots, versions, continuity and a timeline — because that is how films are actually made."],
       ["Bilingual by design", "French and English from day one, with an architecture ready for more languages."]]],
    fr: ["À propos de Studio Movies IA", "Nous construisons la couche d'orchestration, pas un modèle de plus.",
      [["La thèse", "Les modèles vidéo continueront de changer. Ce qui se capitalise, c'est l'orchestration, l'UX, les templates, les prompts, les crédits, les projets et la distribution."],
       ["Indépendance fournisseur", "Chaque modèle vit dans un registre avec son coût, son prix en crédits et son fallback. Un changement de tarif est une configuration."],
       ["Conçu pour la production", "Actes, séquences, scènes, plans, versions, continuité et timeline — parce que c'est ainsi qu'on fabrique un film."],
       ["Bilingue par conception", "Français et anglais dès le premier jour, avec une architecture prête pour d'autres langues."]]],
  },
  contact: {
    en: ["Contact", "Talk to the studio team.",
      [["Sales", "studiomoviesia@gmail.com — plans, volume credits, enterprise terms."],
       ["Support", "studiomoviesia@gmail.com — generations, credits, billing."],
       ["Press", "studiomoviesia@gmail.com"],
       ["Legal", "studiomoviesia@gmail.com — copyright, DMCA, data requests."]]],
    fr: ["Contact", "Parlez à l'équipe du studio.",
      [["Commercial", "studiomoviesia@gmail.com — offres, crédits en volume, conditions entreprise."],
       ["Support", "studiomoviesia@gmail.com — générations, crédits, facturation."],
       ["Presse", "studiomoviesia@gmail.com"],
       ["Juridique", "studiomoviesia@gmail.com — droit d'auteur, DMCA, demandes de données."]]],
  },
  affiliate: {
    en: ["Affiliate programme", "Earn on every production you bring in.",
      [["30% for 12 months", "Recurring commission on subscriptions and credit packs."],
       ["90-day cookie", "Attribution window long enough for creative decision cycles."],
       ["Creator marketplace", "Publish templates, styles, character packs and prompt packs with revenue sharing."],
       ["Assets provided", "Cinematic demo reels, screenshots and prompt libraries you can reuse."]]],
    fr: ["Programme d'affiliation", "Gagnez sur chaque production que vous apportez.",
      [["30 % pendant 12 mois", "Commission récurrente sur les abonnements et les packs de crédits."],
       ["Cookie de 90 jours", "Une fenêtre d'attribution adaptée aux cycles de décision créatifs."],
       ["Marketplace créateurs", "Publiez templates, styles, packs de personnages et de prompts avec partage de revenus."],
       ["Ressources fournies", "Bandes démo, captures et bibliothèques de prompts réutilisables."]]],
  },
  careers: {
    en: ["Careers", "Small team, cinematic ambition.",
      [["Senior product engineer", "React, FastAPI, queues and media pipelines. Remote EU."],
       ["ML/video engineer", "Provider adapters, continuity models, frame-level tooling. Remote EU."],
       ["Creative director", "Template library, prompt systems, house style. Paris or remote."],
       ["Growth / SEO", "Bilingual programmatic content that is actually useful. Remote EU."]]],
    fr: ["Carrières", "Petite équipe, ambition cinématographique.",
      [["Ingénieur produit senior", "React, FastAPI, files d'attente et pipelines média. Télétravail UE."],
       ["Ingénieur ML/vidéo", "Adaptateurs fournisseurs, modèles de continuité, outillage image. Télétravail UE."],
       ["Directeur créatif", "Bibliothèque de templates, systèmes de prompts, style maison. Paris ou télétravail."],
       ["Growth / SEO", "Contenu programmatique bilingue réellement utile. Télétravail UE."]]],
  },
};

export default function InfoPage() {
  const { pathname } = useLocation();
  const page = pathname.split("/").filter(Boolean).pop();
  const { locale } = useI18n();
  const data = C[page]?.[locale];
  if (!data) return <div className="px-6 pt-48 text-center text-[#9194a6]">404</div>;
  const [title, sub, items] = data;

  return (
    <div className="pt-16" data-testid={`info-page-${page}`}>
      <Seo
        title={`${title} — Studio Movies IA`}
        description={sub}
        locale={locale}
        path={`/${locale}/${page}`}
        altPath={`/${locale === "en" ? "fr" : "en"}/${page}`}
      />
      <section className="px-6 pb-16 pt-20 lg:px-10 lg:pt-28">
        <div className="mx-auto max-w-[1400px]">
          <p className="overline">{page}</p>
          <h1 className="mt-4 max-w-[900px] font-display text-4xl font-semibold sm:text-5xl lg:text-6xl">
            {title}
          </h1>
          <p className="mt-6 max-w-[600px] text-base text-[#9194a6] md:text-lg">{sub}</p>
        </div>
      </section>

      <section className="px-6 pb-24 lg:px-10">
        <div className="mx-auto grid max-w-[1400px] gap-px overflow-hidden rounded-2xl border border-[#1a1b26] bg-[#1a1b26] sm:grid-cols-2 lg:grid-cols-3">
          {items.map(([t, d]) => (
            <div key={t} className="bg-[#0b0b11] p-8 transition-colors duration-300 hover:bg-[#101118]">
              <h2 className="font-display text-base font-medium">{t}</h2>
              <p className="mt-3 text-[13.5px] leading-relaxed text-[#9194a6]">{d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 pb-28 lg:px-10">
        <div className="mx-auto max-w-[1400px] rounded-2xl border border-[#212230] bg-[#0b0b11] p-10 text-center">
          <p className="font-display text-2xl font-semibold sm:text-3xl">
            {locale === "fr" ? "Prêt à tourner ?" : "Ready to roll?"}
          </p>
          <Link
            to={`/${locale}/register`}
            className="btn-amber mt-7 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold"
          >
            {locale === "fr" ? "Créer votre première scène" : "Create your first scene"}
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
